/**
 *
 * https://komutteknolojisi.com
 *
 * @author Goncanur Uz <goncanur@komutteknolojisi.com>
 */

import { createSelector } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { call, select } from "redux-saga/effects";
import { RootState } from "../store";
import { ApiParams, ApiUnderTheHoodParams, baseUrl, BodyType } from "./types";

/****************************************************/
/**************** TOKEN LANGUAGE STATES *****************/
/****************************************************/
const localeState = (state: RootState) => state;
export const getApiParams = createSelector([localeState], (state) => {
  return {
    lang: "tr-TR",
    token: state.auth.user?.AccessToken.Token || "",
  };
});
/************************************************/
/**************** SAGA REQUEST  *****************/
/************************************************/
export function* createRequestSaga({
  url,
  method,
  body,
  bodyType,
  lng,
  cancelToken,
}: ApiParams) {
  const { token, lang } = yield select(getApiParams);
  let language = lang;
  //@ts-ignore
  return yield call(createInnerRequestSaga, {
    token,
    lang: lng ? lng : language,
    url,
    method,
    bodyType,
    body,
    cancelToken,
  });
}

/************************************************/
/**************** FORM DATA CONVERTER *****************/
/************************************************/
const getFormData = (data: Object): FormData => {
  let formData = new FormData();
  Object.keys(data).forEach((key) => {
    const val = (data as any)[key];
    if (Array.isArray(val) && val[0] instanceof File) {
      val.forEach((v, i) => {
        formData.append(key, v);
      });
    } else {
      if (Array.isArray(val)) {
        val.forEach((v, i) => {
          if (Object.keys(v).length === 0) {
            formData.append(`${key}[${i}]`, v);
          } else {
            if (typeof v === "object") {
              Object.keys(v).forEach((k) =>
                formData.append(`${key}[${i}].${k}`, v[k])
              );
            } else {
              formData.append(`${key}[${i}]`, v);
            }
          }
        });
      } else if (typeof val === "object" && !(val instanceof File)) {
        Object.keys(val).forEach((k) => formData.append(`${key}.${k}`, val[k]));
      } else formData.append(key, val);
    }
  });

  return formData;
};

/************************************************/
/**************** REQUEST HEADER ****************/
/************************************************/

/************************************************/
/**************** API REQUEST ****************/
/************************************************/
const axiosApi = axios.create({
  baseURL: baseUrl,
});
axiosApi.defaults.headers.common["Content-Type"] = "Multipart/form-data";
axiosApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);
axiosApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      window.location.replace("/logout");
    } else return Promise.reject(error);
  }
);
const getRequestHeaders = (bodyType: BodyType) => {
  const content = {
    "Content-Type": BodyType.formdata
      ? "multipart/form-data"
      : "application/json-patch+json",
  };
  axiosApi.defaults.headers["put"] = content;
  axiosApi.defaults.headers["post"] = content;
  axiosApi.defaults.headers["patch"] = content;
  axiosApi.defaults.headers["put"] = content;
  axiosApi.defaults.headers["delete"] = content;
};

const createInnerRequestSaga = ({
  token,
  lang,
  url,
  method,
  bodyType,
  body,
  cancelToken,
}: ApiParams & ApiUnderTheHoodParams) => {
  bodyType = bodyType ?? BodyType.formdata;
  axiosApi.defaults.headers.common["Authorization"] = "Bearer " + token;
  axiosApi.defaults.headers.common["Accept-Language"] = lang || "en-US";
  getRequestHeaders(bodyType);
  switch (method) {
    case "GET":
      return get(url);
    case "PUT":
      return put(url, body, bodyType);
    case "POST":
      return post(url, body, bodyType);
    case "DELETE":
      return del(url);
    default:
      return get(url);
  }
};

export async function get(url: string, config = {}) {
  return await axiosApi
    .get(url, { ...config })
    .then((response) => response.data);
}

export async function post(url: string, data: any, bodyType: any, config = {}) {
  if (bodyType === BodyType.formdata) {
    return axiosApi
      .postForm(url, getFormData(data), { ...config })
      .then((response) => response.data);
  }
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function put(url: string, data: any, bodyType: any, config = {}) {
  if (bodyType === BodyType.formdata) {
    return axiosApi
      .putForm(url, getFormData(data), { ...config })
      .then((response) => response.data);
  }
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function del(url: string, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => response.data);
}
